import {
  ComplimentaryTrialEndedModal,
  type ComplimentaryTrialEndedModalProps,
  TrialEndUsageClass,
} from '@heidi/commerce';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { atom, useAtom, useSetAtom } from 'jotai';

type EndedUsageClass = ComplimentaryTrialEndedModalProps['usageClass'];

const USAGE_OPTIONS: Array<{ label: string; usageClass: EndedUsageClass }> = [
  { label: 'Moderate (>30%)', usageClass: TrialEndUsageClass.Moderate },
  { label: 'High (>100%)', usageClass: TrialEndUsageClass.High },
];

/** The ended modal is open in its dev preview while this holds a usage class. */
const complimentaryTrialEndedDevPreviewAtom = atom<EndedUsageClass | null>(null);

interface ComplimentaryTrialEndedDevSectionProps {
  /** Called after an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

/** Dev-tools section that opens the upgrade modal shown once complimentary Max usage ends. */
export const ComplimentaryTrialEndedDevSection = ({
  onRequestClose,
}: ComplimentaryTrialEndedDevSectionProps) => {
  const setPreview = useSetAtom(complimentaryTrialEndedDevPreviewAtom);

  return (
    <section aria-labelledby="dev-tools-trial-ended-heading" className="mt-6">
      <TypographyP2SemiBold id="dev-tools-trial-ended-heading" className="mb-2">
        Heidi 2 complimentary usage ended
      </TypographyP2SemiBold>
      <div className="flex flex-col gap-3">
        <TypographyCaption className="text-text-tertiary">
          Opens the upgrade modal by usage during the complimentary period, against the
          plan allowance. Preview only: its buttons just close it.
        </TypographyCaption>
        <div className="flex gap-2">
          {USAGE_OPTIONS.map(({ label, usageClass }) => (
            <Button
              key={usageClass}
              variant="secondary"
              size="xs"
              onClick={() => {
                setPreview(usageClass);
                onRequestClose?.();
              }}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Always-mounted counterpart to {@link ComplimentaryTrialEndedDevSection},
 * rendered by `DevToolsSidebar` outside the sheet so the modal survives the
 * sheet closing.
 */
export const ComplimentaryTrialEndedDevHost = () => {
  const [preview, setPreview] = useAtom(complimentaryTrialEndedDevPreviewAtom);
  const close = () => setPreview(null);

  if (preview === null) return null;

  return (
    <ComplimentaryTrialEndedModal
      open
      usageClass={preview}
      currentPlanName="Scribe Plus"
      targetPlanName="Clinician Max"
      onUpgrade={close}
      onStay={close}
      onDismiss={close}
    />
  );
};
